<template>
  <div class="migration-progress">
    <div class="progress-checklist">
      <div
        v-for="step in steps"
        :key="step.number"
        class="progress-item"
        :class="{
          'completed': isCompleted(step),
          'current': step.number === currentStep
        }"
      >
        <div class="progress-icon">
          <i v-if="isCompleted(step)" class="fas fa-check"></i>
          <i v-else-if="step.number === currentStep" class="fas fa-arrow-right"></i>
          <span v-else class="step-number">{{ step.number }}</span>
        </div>
        <div class="progress-content">
          <div class="progress-title">{{ step.title }}</div>
          <div class="progress-description" v-html="isCompleted(step) ? step.completedText : getStepText(step)">
          </div>
          <template v-if="step.number === 2 && currentStep === 2">
            <a
              v-if="shop.scope_update_required && shop.scope_update_auth_url"
              class="enable-btn"
              :href="shop.scope_update_auth_url"
              target="_blank"
              rel="noopener"
              @click="$emit('open-permissions')"
            >
              Update permissions
            </a>
            <button
              v-else
              class="enable-btn"
              :disabled="continueState === 'preparing'"
              @click="$emit('continue')"
            >
              {{ continueState === 'preparing' ? 'Preparing…' : 'Continue' }}
            </button>
            <p v-if="continueMessage" class="progress-status" :class="{ 'progress-status--error': continueState === 'failed' }">{{ continueMessage }}</p>
          </template>
          <template v-if="step.number === 4 && (shop.upgrade_process_step === 4 || shop.upgrade_process_step === 5)">
            <button
              :class="currentStep === 4 ? 'enable-btn' : 'link-btn'"
              :disabled="reviewState === 'opening'"
              @click="$emit('review')"
            >
              {{ reviewState === 'opening' ? 'Opening…' : (currentStep === 4 ? 'Review your data in the new app' : 'Open your review again') }}
            </button>
            <p v-if="reviewMessage" class="progress-status" :class="{ 'progress-status--error': reviewState === 'failed' }">{{ reviewMessage }}</p>
          </template>
          <button
            v-if="step.number === 5 && shop.upgrade_process_step === 5 && !shop.new_app_activate_agree"
            class="enable-btn"
            @click="$emit('activate')"
          >
            Activate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MigrationProgress',
  props: {
    shop: {
      type: Object,
      required: true
    },
    // From UpgradeBanner: 'idle' | 'preparing' | 'waiting' | 'failed', and the line shown under step 2.
    continueState: {
      type: String,
      default: 'idle'
    },
    continueMessage: {
      type: String,
      default: ''
    },
    // From UpgradeBanner: 'idle' | 'opening' | 'failed', and the line shown under step 4.
    reviewState: {
      type: String,
      default: 'idle'
    },
    reviewMessage: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      steps: [
        {
          number: 1,
          title: 'Offer Secured',
          completedText: 'You\'ve locked in your discount!',
          pendingText: 'Lock in your exclusive pricing'
        },
        {
          number: 2,
          title: 'Permissions Updated',
          completedText: 'App permissions granted!',
          pendingText: '<strong>Action needed</strong> – grant new app access'
        },
        {
          number: 3,
          title: 'Setup & Data Migrated',
          completedText: 'Migration completed by our team!',
          pendingText: 'We are working on this step'
        },
        {
          number: 4,
          title: 'App Access & Review',
          completedText: 'Access and review completed!',
          pendingText: 'Review your data in the new app and approve the switch (live walkthrough available)'
        },
        {
          number: 5,
          title: 'New App Activation',
          completedText: "Activation confirmed – we'll finalize your upgrade right away!",
          pendingText: 'Final step – confirm activation to go live'
        }
      ]
    }
  },
  computed: {
    // The first step not yet done. Usually upgrade_process_step, except that step 2 reopens
    // whenever the new app asks for more permissions (scope_update_required), at any step.
    currentStep () {
      const open = this.steps.find(step => !this.isCompleted(step))
      return open ? open.number : null
    }
  },
  methods: {
    // Step 1 is done once they accepted the offer. Step 2 is done once the new app has the
    // permissions it asked for: the new app sets step 2 when they're granted, and
    // scope_update_required while some are still missing.
    isCompleted (step) {
      if (step.number === 1) return !!this.shop.agree_to_upgrade
      if (step.number === 2) return this.shop.upgrade_process_step >= 2 && !this.shop.scope_update_required
      return step.number < this.shop.upgrade_process_step
    },
    getStepText (step) {
      if (step.number === 2 && this.shop.scope_update_required) {
        return '<strong>Action needed</strong> – approve the new app\'s permissions in Shopify'
      }
      // Approving in the new app's review sets new_app_activate_agree with step 5: nothing left to
      // press here, the switch is ours.
      if (step.number === 5 && this.shop.new_app_activate_agree) {
        return 'Approved – we\'ll switch your store to the new app and email you when it\'s done'
      }
      // Step 4 opens when our team has checked the migrated data (the new app moves the shop to 4).
      if (step.number === 4 && this.shop.upgrade_process_step === 4) {
        return '<strong>Action needed</strong> – check each consignor in the new app and approve the switch (live walkthrough available)'
      }
      return step.pendingText
    }
  }
}
</script>

<style lang="scss" scoped>
// Rendered inside UpgradeBanner's white card, so the palette matches that component
// rather than the surrounding dashboard panels.
.migration-progress {
  --mp-ink: #2a1a63;
  --mp-ink-soft: #514b6b;
  --mp-violet: #6b32d6;
  --mp-violet-line: #ede6f8;
  --mp-muted: #9a95ad;
  --mp-green: #1fa971;
  --mp-gold: #f5b32c;
  --mp-gold-hi: #ffc348;

  color: var(--mp-ink-soft);
}

.progress-checklist {
  display: flex;
  flex-direction: column;
}

.progress-item {
  display: flex;
  align-items: flex-start;
  padding: 11px 0;
  font-size: 15px;
  line-height: 1.3;

  + .progress-item {
    border-top: 1px solid var(--mp-violet-line);
  }

  &.completed {
    .progress-icon {
      background: var(--mp-green);
      color: #fff;
      border: 2px solid var(--mp-green);
    }

    .progress-title {
      color: var(--mp-ink);
      font-weight: 700;
    }

    .progress-description {
      color: var(--mp-ink-soft);
    }
  }

  &.current {
    .progress-icon {
      background: transparent;
      color: var(--mp-violet);
      border: 2px solid var(--mp-violet);
      position: relative;
      animation: mpPulse 2s infinite;

      .fas.fa-arrow-right {
        font-size: 10px;
        line-height: 1;
      }
    }

    .progress-title {
      color: var(--mp-ink);
      font-weight: 700;
    }

    .progress-description {
      color: var(--mp-ink-soft);
      font-weight: 500;
    }
  }

  &:not(.completed):not(.current) {
    .progress-icon {
      background: transparent;
      color: var(--mp-muted);
      border: 2px solid #ded7ee;
    }

    .progress-title {
      color: var(--mp-muted);
    }

    .progress-description {
      color: var(--mp-muted);
    }
  }
}

.progress-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  margin-top: 1px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;

  .fas.fa-check {
    font-size: 10px;
    line-height: 1;
  }

  .step-number {
    font-size: 10px;
    line-height: 1;
  }
}

.progress-content {
  flex: 1;
  min-width: 0;
}

.progress-title {
  font-weight: 700;
  margin-bottom: 2px;
  font-size: 14px;
  line-height: 1.2;
  letter-spacing: -.01em;
}

.progress-description {
  font-size: 12.5px;
  line-height: 1.4;
}

.progress-status {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--mp-ink-soft);

  &--error {
    color: #b42318;
  }
}

.enable-btn {
  display: inline-flex;
  align-items: center;
  background: var(--mp-gold);
  color: #241543; // also an <a> (Update permissions): override the global link colour
  text-decoration: none;
  border: none;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .045em;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 8px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .06);
  transition: background .15s, box-shadow .15s;

  &:hover,
  &:focus {
    color: #241543;
    background: var(--mp-gold-hi);
    box-shadow: 0 6px 16px -8px rgba(180, 120, 0, .65);
  }

  &:focus-visible {
    outline: 2px solid var(--mp-violet);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: .6;
    cursor: default;
    box-shadow: none;
  }
}

// A quieter text button: reopening the review once step 4 is done.
.link-btn {
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: var(--mp-violet);
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: .6;
    cursor: default;
  }
}

@keyframes mpPulse {
  0% {
    transform: scale(1);
    opacity: .6;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: .6;
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-item.current .progress-icon {
    animation: none;
  }

  .enable-btn {
    transition: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/vendorconsignment/components/MigrationProgress.vue